import React, { createContext, useContext, type ReactNode } from 'react';

import type { ZohoTokenHolder } from '../boot/auth-exchange';
import type { CallSessionOrchestrator } from '../call/session';
import type { DeskInvokeClient } from '../zoho/desk-invoke';
import type { PhoneBridgeClient } from '../zoho/phonebridge-client';

import type { AuditClient } from './audit-client';
import type { CallsApiClient } from './calls-api';
import type { TenantsApiClient } from './tenants-api';

/**
 * Services exposed to all widget components via React Context. Replaces the
 * F1 expedient global `(window as ...).__astroline`.
 *
 * F2-G4 extends this with `calls`, `tenants`, `zohoToken`, and `saveWrapUp`.
 * The `zohoToken` field is the holder for the per-tenant Zoho OAuth credentials
 * delivered by /v1/auth/exchange — closing la deuda crítica de F1
 * (VITE_ZOHO_OAUTH_TOKEN ya no se embebe en el bundle).
 */
export interface WidgetServices {
  orch: CallSessionOrchestrator;
  phonebridge: PhoneBridgeClient;
  desk: DeskInvokeClient;
  calls: CallsApiClient;
  tenants: TenantsApiClient;
  zohoToken: ZohoTokenHolder;
  /** F4.5-D2: best-effort audit emission widget→IS. */
  auditClient: AuditClient;
  placeCall: (target: string) => void;
  saveWrapUp: (
    callId: string,
    input: { disposition: string; notes: string },
  ) => Promise<{ ok: boolean; pending: boolean }>;
}

const Ctx = createContext<WidgetServices | null>(null);

export function WidgetServicesProvider({
  services,
  children,
}: {
  services: WidgetServices;
  children: ReactNode;
}): React.JSX.Element {
  return <Ctx.Provider value={services}>{children}</Ctx.Provider>;
}

export function useWidgetServices(): WidgetServices {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWidgetServices must be used inside WidgetServicesProvider');
  return v;
}
